/* What you do to a whole recipe, rather than to one row of it.
 *
 * store.js is generated from the product plan and knows about rows: a recipe,
 * an ingredient, a step, a note. It does not know that a recipe IS its
 * ingredients and its steps and the voices in its margin — so every screen that
 * copies or deletes one has to walk the same four lists in the same order, and
 * get the audio right on the way past. Written inline twice, that is two
 * chances to leave a recording behind or take one that belonged to somebody
 * else. It lives here once instead.
 *
 * Nothing here reaches for the router or a toast: these return what happened —
 * a new id, a count of recordings lost — and the screen decides what to say.
 */

import {
  recipes, ingredients, steps, marginNotes,
  recipesReady, ingredientsReady, stepsReady, marginNotesReady,
  addRecipe, removeRecipe,
  addIngredient, removeIngredient,
  addStep, removeStep,
  addMarginNote, removeMarginNote,
} from '../store.js';
import { getAudioBlob, putAudio, deleteAudio, isAudioRef, isDurableMedia } from './audio-store.js';

/* Ids arrive from three places — a row, a route param, a Set of strings — and
   only one of them is a number. `===` on a raw id is the bug where a recipe
   looks empty because its ingredients were filed under '7' and asked for as 7. */
const same = (a, b) => String(a) === String(b);

/* Position is the order she gave them; array order is the order IndexedDB
   happened to hand them back. Not the same thing. */
const byPosition = (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0);
const byTime = (a, b) => String(a.recordedAt ?? '').localeCompare(String(b.recordedAt ?? ''));

/** Everything hanging off one recipe, each list in its reading order. */
export function recipeContents(recipeId) {
  const recipe = recipes.value.find((r) => same(r.id, recipeId)) ?? null;
  return {
    recipe,
    ingredients: ingredients.value.filter((i) => same(i.recipeId, recipeId)).slice().sort(byPosition),
    steps: steps.value.filter((s) => same(s.recipeId, recipeId)).slice().sort(byPosition),
    notes: marginNotes.value.filter((n) => same(n.recipeId, recipeId)).slice().sort(byTime),
  };
}

/* Both operations below read the whole store before they write to it, so both
   wait for it. A duplicate fired from a screen that rendered before hydration
   finished would otherwise copy a recipe with no steps and no notes and call
   that a copy — and a delete would leave the rows it could not yet see. */
const hydrated = () => Promise.all([
  recipesReady, ingredientsReady, stepsReady, marginNotesReady,
]);

/**
 * A second copy of a recipe in the same cook's book, to change without
 * touching hers.
 *
 * @param {string|number} recipeId
 * @returns {Promise<string|number|null>} the new recipe's id, or null if the
 *   source was already gone.
 */
export async function duplicateRecipe(recipeId) {
  await hydrated();

  const { recipe, ingredients: sourceIngredients, steps: sourceSteps, notes } = recipeContents(recipeId);
  if (!recipe) return null;

  const copy = addRecipe({
    title: `${recipe.title || 'Untitled'} (copy)`,
    cookId: recipe.cookId,
    occasion: recipe.occasion,
    serves: recipe.serves,
    /* A copy is not mid-recording. `capturing` is the state of a sheet somebody
       is still filling in from a voice in the room, and a copy made months
       later inherits the finished recipe, not the act of taking it down. */
    status: 'kept',
    /* Kept, not stamped with now: this is still the day she gave it to you. The
       copy is new, the recipe is not. */
    capturedAt: recipe.capturedAt,
  });

  for (const row of sourceIngredients) {
    addIngredient({
      recipeId: copy.id,
      amount: row.amount,
      unit: row.unit,
      quantity: row.quantity,
      item: row.item,
      position: row.position,
    });
  }

  /* The new steps get new ids, so every note pointing at an old one has to be
     re-aimed. Without this map the copy's margin notes hang off HER steps: edit
     the copy's step 3 and the note stays beside the original. */
  const newStepId = new Map();
  for (const row of sourceSteps) {
    const made = addStep({ recipeId: copy.id, body: row.body, position: row.position });
    newStepId.set(String(row.id), made.id);
  }

  for (const note of notes) {
    addMarginNote({
      recipeId: copy.id,
      /* A note whose step is already gone keeps an empty stepId, which the
         sheet reads as a recipe-level note and shows at the top of the margin.
         Losing the note would be worse than showing it slightly adrift. */
      stepId: newStepId.get(String(note.stepId)) ?? '',
      kind: note.kind,
      body: note.body,
      mediaUrl: await copyMedia(note.mediaUrl),
      durationSeconds: note.durationSeconds,
      /* Her clock, not ours: recordedAt is what orders the margin. */
      recordedAt: note.recordedAt,
    });
  }

  return copy.id;
}

/**
 * A reference to a SECOND copy of the same bytes.
 *
 * Sharing one `idb:` reference between two notes is a bug with a fuse in it:
 * both copies play until somebody deletes either one, and then the other note
 * still lists a recording it can no longer play. Nothing warns, and the voice
 * is gone. Storing the blob again costs a few hundred kilobytes and makes the
 * two recipes genuinely independent.
 *
 * Returns '' when there is nothing to copy — a note with its written body and
 * no recording is honest; a note pointing at the original's audio is not.
 */
async function copyMedia(ref) {
  /* Only our own IndexedDB references are duplicated. An http(s) or data: URL
     is not ours to own or to delete, so two notes may share it safely. */
  if (!isAudioRef(ref)) return isDurableMedia(ref) ? ref : '';

  /* getAudioBlob hands back the stored Blob directly — no object URL round
     trip, so no URL to leak and no fetch that a CSP could refuse. */
  const blob = await getAudioBlob(ref);
  if (!blob) return '';

  try {
    return await putAudio(blob);
  } catch {
    /* Out of quota, or no IndexedDB at all. The written note is still worth
       keeping; a mediaUrl we could not write behind it is not. */
    return '';
  }
}

/**
 * Remove a recipe and everything that only existed because of it.
 *
 * @param {string|number} recipeId
 * @returns {Promise<number>} how many recordings were deleted, so the caller
 *   can say what is actually gone rather than "recipe deleted".
 */
export async function deleteRecipeDeep(recipeId) {
  await hydrated();

  const { recipe, ingredients: rows, steps: stepRows, notes } = recipeContents(recipeId);
  if (!recipe) return 0;

  let recordings = 0;
  for (const note of notes) {
    if (isDurableMedia(note.mediaUrl)) {
      /* Each in its own try/catch: one unreachable blob must not leave the
         remaining notes, or the recipe, half-deleted. The row goes either way —
         a note whose audio survives it is invisible and unplayable. */
      try {
        if (await deleteAudio(note.mediaUrl)) recordings += 1;
      } catch { /* already gone, or a store we cannot open */ }
    }
    try { removeMarginNote(note.id); } catch { /* nothing left to remove */ }
  }

  for (const row of rows) removeIngredient(row.id);
  for (const row of stepRows) removeStep(row.id);

  /* The recipe row LAST. Deleting it first and then failing part way through
     the children leaves ingredients and notes filed under an id no screen can
     reach: invisible, undeletable, and still taking up the store. */
  removeRecipe(recipe.id);

  return recordings;
}
